import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { DangerHexagon, DangerTriangle, CheckCircle } from "@mynaui/icons-react";
import assetService from '../../../services/assetService';

const mapAgentResponse = (d) => {
  const alertModal = d.alertModal ?? d.AlertModal;
  const aiReport   = d.aiAnalysisReport ?? d.AiAnalysisReport;
  const raiseWorkOrder = d.raiseWorkOrder ?? d.RaiseWorkOrder;
  const keyFindings = aiReport?.keyFindings ?? aiReport?.KeyFindings ?? {};

  return {
    scenario: raiseWorkOrder ? 'failure' : 'healthy',
    diagnosis: {
      title:            alertModal?.faultTitle        ?? alertModal?.FaultTitle        ?? 'Health Check Complete',
      description:      aiReport?.summary             ?? aiReport?.Summary             ?? '',
      aiAnalysisContent: aiReport?.aiNotesForTechnician ?? aiReport?.AiNotesForTechnician ?? '',
      warning:          alertModal?.urgentAction      ?? alertModal?.UrgentAction      ?? alertModal?.faultDescription ?? alertModal?.FaultDescription ?? '',
      details:          keyFindings?.rootCause        ?? keyFindings?.RootCause        ?? '',
    },
    keyFindings: {
      rootCause:            keyFindings?.rootCause            ?? keyFindings?.RootCause            ?? '',
      severity:             keyFindings?.severity             ?? keyFindings?.Severity             ?? '',
      anomalyFirstDetected: keyFindings?.anomalyFirstDetected ?? keyFindings?.AnomalyFirstDetected ?? '',
    },
    contributingFactors: aiReport?.contributingFactors ?? aiReport?.ContributingFactors ?? null,
    peakValues:          d.peakValues                  ?? d.PeakValues                  ?? [],
    sensorReadings: [],
  };
};

const MAX_POLL_MS  = 120000; 
const POLL_INTERVAL_MS = 2000;  

const HealthCheckModal = ({ open, onClose, assetId, onHealthCheckComplete }) => {
  const [stage, setStage] = useState('checking');
  const [scenario, setScenario] = useState(null);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !assetId) return;

    setStage('checking');
    setScenario(null);
    setNotes('');
    setConfirmed(false);
    setHealthData(null);
    setErrorMessage('');

    let cancelled    = false;
    let pollInterval = null;
    let pollTimeout  = null; 

    const timer1 = setTimeout(() => {
      if (!cancelled) setStage('diagnosing');
    }, 2000);

    const stopPolling = () => {
      if (pollInterval) clearInterval(pollInterval);
      if (pollTimeout)  clearTimeout(pollTimeout);
    };

    // Submit diagnose job, get requestId immediately
    // If user refreshed and a processing request exists, same requestId is returned
    assetService.diagnoseAsset(assetId)
      .then((res) => {
        if (cancelled) return;
        clearTimeout(timer1);

        const data      = res.data?.data || res.data;
        const requestId = data?.requestId;

        if (!requestId) throw new Error('No requestId returned');

        setStage('diagnosing');

        // Poll every 2 seconds
        pollInterval = setInterval(async () => {
          if (cancelled) return;

          try {
            const statusRes  = await assetService.pollDiagnosticStatus(requestId);
            const statusData = statusRes.data?.data || statusRes.data;

            if (statusData.status === 'processing') return; // keep polling

            stopPolling();

            if (statusData.status === 'error') {
              if (!cancelled) {
                setErrorMessage(statusData.error || 'Diagnosis failed. Please try again.');
                setStage('error');
              }
              return;
            }

            if (statusData.status === 'complete' && !cancelled) {
              const raw = typeof statusData.response === 'string'
                ? JSON.parse(statusData.response)
                : statusData.response;

              if (raw?.found === false) {
                setErrorMessage(
                  raw?.error || 'No sensor data found for this asset.'
                );
                setStage('error');
                return;
              }

              const result = mapAgentResponse(raw);
              setHealthData(result);
              setScenario(result.scenario);
              setStage('result');
            }
          } catch {
            if (!cancelled) {
              stopPolling();
              setStage('error');
            }
          }
        }, POLL_INTERVAL_MS);

        // Safety net: stop polling after MAX_POLL_MS
        pollTimeout = setTimeout(() => {
          if (!cancelled) {
            stopPolling();
            setErrorMessage('Diagnosis timed out. Please try again.');
            setStage('error');
          }
        }, MAX_POLL_MS);
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timer1);
          setErrorMessage('Unable to connect to the AI agent. Please try again.');
          setStage('error');
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timer1);
      stopPolling();
    };
  }, [open, assetId]);

  const handleClose = () => {
    setStage('checking');
    setScenario(null);
    setNotes('');
    setConfirmed(false);
    setSubmitting(false);
    onClose();
  };

  const handleRequestApproval = async () => {
    setSubmitting(true);
    try {
      await assetService.createWorkOrder(assetId, {
        title: healthData?.diagnosis.title,
        actionPlan: notes,
      });
      enqueueSnackbar('Work order created successfully.', { variant: 'success' });
      handleClose();
      if (onHealthCheckComplete) onHealthCheckComplete(healthData);
    } catch {
      // apiClient interceptor already shows the error snackbar
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitIncorrect = () => {
    console.log('Submit incorrect assessment:', { assetId, notes, confirmed });
    handleClose();
  };

  // ── Modal-level colored header ────────────────────────────────────────────────

  const showAnalysisHeader = stage === 'result' && (scenario === 'failure' || scenario === 'healthy');
  const headerColor = healthData?.scenario === 'failure' ? '#C62828' : '#2E7D32';

  const renderModalHeader = () => (
    <Box
      sx={{
        background: headerColor,
        px: 3, py: 2.5,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      <Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', mb: 0.4, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          AI Analysis Report
        </Typography>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}>
          {healthData?.diagnosis.title}
        </Typography>
      </Box>
      <IconButton
        onClick={handleClose}
        sx={{ color: '#fff', mt: -0.5, mr: -1, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
      >
        <Close />
      </IconButton>
    </Box>
  );

  // ── Loading ───────────────────────────────────────────────────────────────────

  const renderLoadingScreen = (title, subtitle) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', py: 6 }}>
      <CircularProgress size={60} sx={{ mb: 3, color: '#2E7D32' }} />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Typography variant="body1" sx={{ color: '#757575' }}>{subtitle}</Typography>
    </Box>
  );

  // ── AI Analysis ───────────────────────────────────────────────────────────────

  const renderAiAnalysis = () => {
    const accentColor = headerColor;
    return (
      <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', mb: 3 }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Summary</Typography>
          <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.7 }}>
            {healthData?.diagnosis.description}
          </Typography>
        </Box>

        <Box sx={{ borderTop: '1px solid #F3F4F6', mx: 2.5 }} />

        <Box sx={{ px: 2.5, pt: 1.5, pb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Key Findings</Typography>
          {[
            { label: 'Root Cause', value: healthData?.keyFindings?.rootCause || healthData?.diagnosis.details || healthData?.diagnosis.title },
            {
              label: 'Severity',
              value: healthData?.keyFindings?.severity || (
                healthData?.scenario === 'failure'
                  ? 'Critical \u2014 Immediate replacement recommended'
                  : 'Low \u2014 Continue scheduled maintenance'
              ),
            },
            { label: 'Anomaly First Detected', value: healthData?.keyFindings?.anomalyFirstDetected || '\u2014' },
          ].map((row, i, arr) => (
            <Box key={i}>
              <Box sx={{ display: 'flex', py: 0.75, gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 150, color: '#111827' }}>{row.label}</Typography>
                <Typography variant="body2" sx={{ color: '#4B5563', flex: 1 }}>{row.value}</Typography>
              </Box>
              {i < arr.length - 1 && <Box sx={{ borderTop: '1px solid #F9FAFB' }} />}
            </Box>
          ))}
        </Box>

        {healthData?.contributingFactors && (
          <>
            <Box sx={{ borderTop: '1px solid #F3F4F6', mx: 2.5 }} />
            <Box sx={{ px: 2.5, pt: 1.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Contributing Factors</Typography>
              {Array.isArray(healthData.contributingFactors)
                ? healthData.contributingFactors.map((factor, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 0.75 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, mt: '5px', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6 }}>
                        {typeof factor === 'string' ? factor : JSON.stringify(factor)}
                      </Typography>
                    </Box>
                  ))
                : <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.7 }}>{healthData.contributingFactors}</Typography>
              }
            </Box>
          </>
        )}

        <Box sx={{ borderTop: '1px solid #F3F4F6', mx: 2.5 }} />
        <Box sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>AI Notes for Technician</Typography>
          <Box sx={{ p: 1.5, borderRadius: '8px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
            <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.8 }}>
              {healthData?.diagnosis.aiAnalysisContent}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Healthy ───────────────────────────────────────────────────────────────────

  const renderHealthyResult = () => (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        <CheckCircle style={{ fontSize: 48, color: '#2E7D32' }} />
      </Box>
      {renderAiAnalysis()}
      <Button
        fullWidth variant="contained" onClick={handleClose}
        sx={{ backgroundColor: '#212121', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#000' } }}
      >
        ← Go back
      </Button>
    </Box>
  );

  // ── Failure step 1 ────────────────────────────────────────────────────────────

  const renderFailureResult = () => (
    <Box>
      <Alert
        icon={<DangerTriangle style={{ fontSize: 22, color: '#E65100' }} />}
        sx={{ backgroundColor: '#FFF3E0', border: '1px solid #FFE0B2', mb: 3, '& .MuiAlert-message': { color: '#E65100', fontSize: '0.875rem' } }}
      >
        {healthData?.diagnosis.warning}
      </Alert>
      {renderAiAnalysis()}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth variant="contained"
          onClick={() => { setNotes(''); setConfirmed(false); setScenario('request'); }}
          sx={{ backgroundColor: '#2E7D32', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#1B5E20' } }}
        >
          Proceed to Request Creation
        </Button>
        <Button
          fullWidth variant="outlined"
          onClick={() => { setNotes(''); setConfirmed(false); setScenario('incorrect'); }}
          sx={{ borderColor: '#212121', color: '#212121', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { borderColor: '#000', backgroundColor: '#F5F5F5' } }}
        >
          Incorrect Agent Assessment ?
        </Button>
      </Box>
    </Box>
  );

  // ── Failure step 2: Request creation ─────────────────────────────────────────

  const renderRequestCreation = () => (
    <Box sx={{ position: 'relative' }}>
      <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0 }}>
        <Close />
      </IconButton>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, pr: 4 }}>Create Work Order Request</Typography>
      <Typography variant="body2" sx={{ color: '#757575', mb: 3 }}>
        {healthData?.diagnosis.title}
      </Typography>
      <TextField
        fullWidth multiline rows={4}
        placeholder="Add action plan / notes..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            sx={{ color: '#1976D2', '&.Mui-checked': { color: '#1976D2' } }}
          />
        }
        label={<Typography variant="body2">I have verified all reported issues and confirm that this asset is affected.</Typography>}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        <Button
          fullWidth variant="contained"
          disabled={!confirmed || !notes.trim() || submitting}
          onClick={handleRequestApproval}
          sx={{ backgroundColor: '#2E7D32', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#1B5E20' }, '&:disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' } }}
        >
          {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Request Approval'}
        </Button>
        <Button
          fullWidth variant="outlined"
          disabled={submitting}
          onClick={() => setScenario('failure')}
          sx={{ borderColor: '#212121', color: '#212121', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { borderColor: '#000', backgroundColor: '#F5F5F5' } }}
        >
          ← Go back
        </Button>
      </Box>
    </Box>
  );

  // ── Incorrect assessment ──────────────────────────────────────────────────────

  const renderIncorrectResult = () => (
    <Box sx={{ position: 'relative' }}>
      <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0 }}>
        <Close />
      </IconButton>
      <Box sx={{ textAlign: 'center', mb: 3, pt: 1 }}>
        <DangerHexagon style={{ fontSize: 50, color: '#C62828' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Incorrect Assessment</Typography>
        <Typography variant="body2" sx={{ color: '#757575' }}>
          Submit a thorough explanation outlining why the assumption is inaccurate.
        </Typography>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#424242', mb: 2 }}>
          The physical inspection did not reveal any concerns, indicating the agent's assumption was wrong.
        </Typography>
        <TextField
          fullWidth multiline rows={5}
          placeholder="Explain why the assessment is incorrect..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{ color: '#1976D2', '&.Mui-checked': { color: '#1976D2' } }}
            />
          }
          label={<Typography variant="body2">I have verified all reported issues and confirm that this asset is not affected.</Typography>}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth variant="contained"
          disabled={!confirmed || !notes.trim()}
          onClick={handleSubmitIncorrect}
          sx={{ backgroundColor: '#2E7D32', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { backgroundColor: '#1B5E20' }, '&:disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' } }}
        >
          Submit
        </Button>
        <Button
          fullWidth variant="outlined"
          onClick={() => setScenario('failure')}
          sx={{ borderColor: '#212121', color: '#212121', textTransform: 'none', fontWeight: 600, py: 1.5, '&:hover': { borderColor: '#000', backgroundColor: '#F5F5F5' } }}
        >
          ← Go back
        </Button>
      </Box>
    </Box>
  );

  // ── Error ─────────────────────────────────────────────────────────────────────

  const renderErrorScreen = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', py: 6 }}>
      <DangerHexagon style={{ fontSize: 48, color: '#C62828', marginBottom: 16 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Diagnosis Unavailable</Typography>
      <Typography variant="body2" sx={{ color: '#757575', mb: 3, textAlign: 'center' }}>
        {errorMessage || 'Unable to connect to the AI agent. Please try again.'}
      </Typography>
      <Button variant="outlined" onClick={handleClose}
        sx={{ textTransform: 'none', borderColor: '#212121', color: '#212121' }}>
        Close
      </Button>
    </Box>
  );

  // ── Router ────────────────────────────────────────────────────────────────────

  const renderContent = () => {
    if (stage === 'error')      return renderErrorScreen();
    if (stage === 'checking')   return renderLoadingScreen('Checking System Health', 'Verifying Sensors data');
    if (stage === 'diagnosing') return renderLoadingScreen('Diagnosing', 'Analysing with AI Agent');

    if (stage === 'result') {
      if (scenario === 'incorrect') return renderIncorrectResult();
      if (scenario === 'request')   return renderRequestCreation();
      if (scenario === 'healthy')   return renderHealthyResult();
      return renderFailureResult();
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' } } }}
    >
      {showAnalysisHeader && renderModalHeader()}
      <DialogContent sx={{ p: 3, maxHeight: '80vh', overflowY: 'auto' }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default HealthCheckModal;